import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { userService, type User } from '@/services/userService'
import AddUserModal, { type AddUserModalRef } from '@/components/modals/AddUserModal'
import DeleteUserModal, { type DeleteUserModalRef } from '@/components/modals/DeleteUserModal'
import ResetPasswordModal, { type ResetPasswordModalRef } from '@/components/modals/ResetPasswordModal'
import PlatformUserDetailsModal, { type PlatformUserDetailsModalRef } from '@/components/modals/PlatformUserDetailsModal'
import ChangeRoleModal, { type ChangeRoleModalRef } from '@/components/modals/ChangeRoleModal'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import CustomizableTable, { type TableColumn } from '@/components/ui/CustomizableTable'
import { HttpError } from '@/lib/httpError'

export default function PlatformUsersPage() {
  const { t, i18n } = useTranslation('platformUsers')
  const { user: currentUser, refreshAuth } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false) // Track if we're searching

  // Refs to track latest values without causing re-renders
  const currentPageRef = useRef(currentPage)
  const searchQueryRef = useRef(searchQuery)

  // Update refs when state changes
  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  useEffect(() => {
    searchQueryRef.current = searchQuery
  }, [searchQuery])

  const addUserModalRef = useRef<AddUserModalRef>(null)
  const deleteUserModalRef = useRef<DeleteUserModalRef>(null)
  const resetPasswordModalRef = useRef<ResetPasswordModalRef>(null)
  const userDetailsModalRef = useRef<PlatformUserDetailsModalRef>(null)
  const changeRoleModalRef = useRef<ChangeRoleModalRef>(null)

  // Check if user has access to platform users page
  const hasAccess = currentUser?.platform_role && ['owner', 'admin', 'security', 'auditor'].includes(currentUser.platform_role)
  const isOwner = currentUser?.platform_role === 'owner';
  const isAdmin = currentUser?.platform_role === 'admin' || false;
  const isSecurityOrAuditor = currentUser?.platform_role === 'security' || currentUser?.platform_role === 'auditor'

  const fetchUsers = useCallback(async (page?: number, query?: string, isSearch: boolean = false) => {
    // For search operations, use isSearching instead of loading
    if (isSearch) {
      setIsSearching(true)
    } else {
      setLoading(true)
    }
    setError('')

    // Use current state values if not provided
    const actualPage = page ?? currentPageRef.current
    const actualQuery = query ?? searchQueryRef.current

    // Ensure minimum 2 seconds loading time for better UX (both success and error)
    const startTime = Date.now()

    try {
      // Calculate offset from page number (page is 1-indexed)
      const offset = (actualPage - 1) * pageSize
      const response = await userService.getUsers({
        limit: pageSize,
        offset,
        q: actualQuery || undefined
      })
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      setUsers(response.users || [])
      setTotalItems(response.total || 0)
      setCurrentPage(actualPage)
    } catch (err) {
      // Handle 401 error: try to refresh token
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry fetching users after successful refresh
          return fetchUsers(actualPage, actualQuery, isSearch)
        } catch (refreshError) {
          // Refresh failed, ensure at least 2 seconds have passed before showing error
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, 2000 - elapsed)

          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }

          const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : 'errors.sessionExpired'
          if (refreshErrorMessage.startsWith('errors.')) {
            setError(t(refreshErrorMessage, refreshErrorMessage))
          } else {
            setError(refreshErrorMessage)
          }
          setLoading(false)
          return
        }
      }

      // For other errors, ensure at least 2 seconds have passed before showing error
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      if (isSearch) {
        setIsSearching(false)
      } else {
        setLoading(false)
      }
    }
  }, [pageSize, t, refreshAuth])

  useEffect(() => {
    if (hasAccess) {
      fetchUsers(1)
    }
  }, [hasAccess, fetchUsers])

  // Handle search change from table
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query)
    setCurrentPage(1) // Reset to first page when searching
    fetchUsers(1, query, true) // true = isSearch
  }, [fetchUsers]) // Depend on fetchUsers

  // If user doesn't have access, show access denied
  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto p-6 h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-7V8m0 0V6m0 2H9m3 0h3M5 12a7 7 0 1114 0A7 7 0 015 12z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('accessDenied.title', 'Access Denied')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('accessDenied.description', 'You do not have permission to access this page')}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left max-w-md mx-auto">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-yellow-800">
                  {t('accessDenied.currentRole', 'Current Role')}
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  {t('accessDenied.roleInfo', 'Your role: {{role}}', { role: currentUser?.platform_role || 'unknown' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'admin':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200'
      case 'auditor':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'security':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'user':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'local':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'ldap':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'oidc':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = i18n.language === 'zh' ? 'zh-TW' : 'en-US'

      const formattedDate = date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })

      return formattedDate
    } catch {
      return dateString
    }
  }

  // Define table columns
  const columns: TableColumn<User>[] = [
    {
      id: 'account',
      label: t('table.account', 'Account'),
      accessor: 'account',
      sortable: true,
      searchable: true,
      alwaysVisible: true,
      render: (value) => (
        <div className="text-sm font-semibold text-gray-900">{value}</div>
      )
    },
    {
      id: 'name',
      label: t('table.name', 'Name'),
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-sm text-gray-900">{value}</div>
      )
    },
    {
      id: 'platform_role',
      label: t('table.role', 'Role'),
      accessor: 'platform_role',
      sortable: true,
      filterable: true,
      filterOptions: ['owner', 'admin', 'auditor', 'security', 'user'],
      render: (value) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleColor(value)}`}>
          {String(t(`roles.${value}`, value))}
        </span>
      )
    },
    {
      id: 'type',
      label: t('table.type', 'Type'),
      accessor: 'type',
      sortable: true,
      filterable: true,
      filterOptions: ['local', 'ldap', 'oidc'],
      render: (value) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(value)}`}>
          {String(t(`types.${value}`, value))}
        </span>
      )
    },
    {
      id: 'totp_enabled',
      label: t('table.totpEnabled', '2FA'),
      accessor: 'totp_enabled',
      sortable: true,
      filterable: true,
      filterOptions: ['true', 'false'],
      render: (value) => (
        <div className="flex items-center justify-center">
          {value ? (
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      )
    },
    {
      id: 'contract_agreement',
      label: t('table.contractAgreement', 'Terms Agreement'),
      accessor: 'contract_agreement',
      sortable: true,
      filterable: true,
      filterOptions: ['true', 'false'],
      render: (value) => (
        <div className="flex items-center justify-center">
          {value ? (
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      )
    },
    {
      id: 'last_login_at',
      label: t('table.lastLoginAt', 'Last Login'),
      accessor: 'last_login_at',
      sortable: true,
      defaultVisible: false,
      sortValue: (user) => user.last_login_at ? new Date(user.last_login_at).getTime() : 0,
      render: (value) => (
        <div className="text-sm text-gray-600">{value ? formatDate(value) : '-'}</div>
      )
    },
    {
      id: 'created_at',
      label: t('table.createdAt', 'Created At'),
      accessor: 'created_at',
      sortable: true,
      defaultVisible: false,
      sortValue: (user) => new Date(user.created_at).getTime(),
      render: (value) => (
        <div className="text-sm text-gray-600">{formatDate(value)}</div>
      )
    },
    {
      id: 'updated_at',
      label: t('table.updatedAt', 'Updated At'),
      accessor: 'updated_at',
      sortable: true,
      defaultVisible: false,
      sortValue: (user) => new Date(user.updated_at).getTime(),
      render: (value) => (
        <div className="text-sm text-gray-600">{formatDate(value)}</div>
      )
    },
    {
      id: 'actions',
      label: t('table.actions', 'Actions'),
      accessor: () => null,
      alwaysVisible: true,
      render: (_, user) => {
        const isSelf = user.id === currentUser?.id

        // Permissions:
        // - owner: all actions (view, change-role, reset-password, delete) except on self
        // - admin: view, change-role except on self
        // - security/auditor: view only
        const canView = true // Everyone can view
        const canChangeRole = (isOwner || isAdmin) && !isSelf // Cannot change own role
        const canResetPassword = isOwner && user.type === 'local' && !isSelf
        const canDelete = isOwner && !isSelf // Only owner can delete

        return (
          <div className="flex items-center justify-end gap-1">
            {/* View button - everyone can see */}
            <button
              onClick={() => userDetailsModalRef.current?.open(user)}
              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
              title={t('actions.view', 'View')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>

            {/* Change Role button - owner and admin only */}
            {canChangeRole && (
              <button
                onClick={() => changeRoleModalRef.current?.open(user)}
                className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                title={t('actions.changeRole', 'Change Role')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            )}

            {/* Reset Password button - owner only */}
            {canResetPassword && (
              <button
                onClick={() => resetPasswordModalRef.current?.open(user)}
                className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                title={t('actions.resetPassword', 'Reset Password')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </button>
            )}

            {/* Delete button - owner only */}
            {canDelete && (
              <button
                onClick={() => deleteUserModalRef.current?.open(user)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title={t('actions.delete', 'Delete')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )
      }
    }
  ]

  return (
    <div className="absolute inset-0">
      {/* Full Screen Loading Overlay */}
      {loading && !error && <LoadingOverlay message={t('loading', 'Loading users...')} fullScreen />}

      {/* Full Screen Error Display */}
      {error && !loading && (
        <ErrorDisplay
          error={error}
          title={t('errors.title', 'Error')}
          onRefresh={() => fetchUsers()}
          fullScreen
        />
      )}

      {/* Content Container */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4 h-full flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="mb-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Platform Users')}</h1>
            <p className="text-gray-600 mt-2">{t('description', 'Manage platform users and permissions')}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchUsers()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{t('actions.refresh', 'Refresh')}</span>
            </button>
            {(isOwner || isAdmin) && (
              <button
                onClick={() => addUserModalRef.current?.open()}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>{t('actions.addUser', 'Add User')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content - CustomizableTable */}
      {!error && (
        <CustomizableTable
          data={users}
          columns={columns}
          loading={loading}
          emptyMessage={t('empty.description', 'Get started by adding your first user')}
          pagination={{
            currentPage,
            totalItems,
            pageSize,
            onPageChange: fetchUsers
          }}
          searchMode="server"
          onSearchChange={handleSearchChange}
        />
      )}

      {/* Modals */}
      <AddUserModal ref={addUserModalRef} currentUserRole={currentUser?.platform_role || 'user'} onSuccess={() => fetchUsers()} />
      <DeleteUserModal ref={deleteUserModalRef} onSuccess={() => fetchUsers()} />
      <ResetPasswordModal ref={resetPasswordModalRef} onSuccess={() => fetchUsers()} />
      <PlatformUserDetailsModal ref={userDetailsModalRef} onSuccess={() => fetchUsers()} />
      <ChangeRoleModal ref={changeRoleModalRef} currentUserRole={currentUser?.platform_role || 'user'} onSuccess={() => fetchUsers()} />
      </div>
    </div>
  )
}
